import { View } from '@adornis/data-view/db/view.js';
import { FieldConfiguration } from '@adornis/field-management/field-configuration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { ListAspect } from '@adornis/filter/aspect-schemas/ListAspect.js';
import { ClassBookingFilterAspect } from '../../../bookings/api/ClassBookingFilterAspect.js';
import { GroupFilterAspect } from '../../../groups/GroupFilterAspect.js';
import { RoleFilterAspect } from '../../../permissions/api/RoleFilterAspect.js';
import type { Person } from '../../entities/person.js';

/**
 * Several Standard Configuration for the UserList
 * Including default columns filter enrichments and modular components of the standardview
 */

/**
 * The standard field configuration for the user list
 */
export const UserListStandardFieldConfiguration = [
  new FieldConfiguration({
    fieldName: 'profilDaten.stammdaten.vorname',
    columnWidth: '250px',
  }),
  new FieldConfiguration({
    fieldName: 'profilDaten.stammdaten.nachname',
    columnWidth: '250px',
  }),
  new FieldConfiguration({
    fieldName: 'email',
    columnWidth: '400px',
  }),
  new FieldConfiguration({
    fieldName: 'mitarbeiterDaten.personalNr',
    columnWidth: '75px',
  }),
  new FieldConfiguration({
    fieldName: 'debitorennummer',
    columnWidth: '180px',
  }),
  new FieldConfiguration({
    fieldName: 'roles.name',
    columnWidth: '220px',
  }),
  new FieldConfiguration({
    fieldName: 'roles.contexts',
    columnWidth: '450px',
  }),
];

/**
 * The standard filter aspects for the user list
 */
export const UserListStandardFilterAspects = [
  new ListAspect({
    fieldName: 'mitarbeiterDaten.anBeschaeftigung.abrechnungsgruppe',
    mode: 'containsOR',
    values: [],
  }),
  new RoleFilterAspect({
    fieldName: 'roles',
    value: [],
  }),
  new GroupFilterAspect({
    fieldName: 'activeBuchungen.groups',
    mode: 'or',
  }),
  new ClassBookingFilterAspect({
    fieldName: 'activeBuchungen',
  }),
];

/**
 * The standard sorting behavior for the user list
 */
export const UserListStandardSorting: {
  sortingFields: string[];
  sortingDirections: ('asc' | 'desc')[];
} = {
  sortingFields: ['debitorennummer'],
  sortingDirections: ['desc'],
};

/**
 * The standard filter for the user list
 * This is the default filter that is used when no other filter is specified
 */
export const UserListStandardFilter = new AdornisFilter({
  ...UserListStandardSorting,
  aspects: UserListStandardFilterAspects,
});

/**
 * The standard view for the user list
 */
export const UserListStandardView = new View<typeof Person>({
  filter: UserListStandardFilter,
  configuredColumns: UserListStandardFieldConfiguration,
});

/**
 * Enriches the filter with the default filter so the user always has the standard filter available. The user may change the default filter by deleting it and writing other values to it.
 * This function ensures the three standard filters are always present. Even if their values are empty.
 * @param view the view to be enriched
 */
export function enrichFilterWithDefaultFilter(view: View<typeof Person>) {
  if (!view.filter) return;
  if (view.filter.getFilterAspect('mitarbeiterDaten.anBeschaeftigung.abrechnungsgruppe') === undefined) {
    view.filter.setFilterAspect(
      new ListAspect({
        fieldName: 'mitarbeiterDaten.anBeschaeftigung.abrechnungsgruppe',
        mode: 'containsOR',
        values: [],
      }),
    );
  }
  if (view.filter.getFilterAspect('roles') === undefined) {
    view.filter.setFilterAspect(new RoleFilterAspect({ fieldName: 'roles', value: [] }));
  }

  if (view.filter.getFilterAspect('buchungen') === undefined) {
    view.filter.setFilterAspect(new ClassBookingFilterAspect({ fieldName: 'activeBuchungen' }));
  }

  /**
   *  sort the aspects for better user experience so the array is [roles.name, roles.contexts, mitarbeiterDaten.anBeschaeftigung.abrechnungsgruppe]
   */
  view.filter.aspects.sort((a, b) => {
    const fieldOrder = ['mitarbeiterDaten.anBeschaeftigung.abrechnungsgruppe', 'roles', 'buchungen'];
    const aIndex = fieldOrder.indexOf(a.fieldName ?? '');
    const bIndex = fieldOrder.indexOf(b.fieldName ?? '');

    // If both fields are in our predefined order, sort by that order
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    // If only one field is in our predefined order, prioritize it
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    // For fields not in our predefined order, maintain their relative position
    return 0;
  });
}
